import { useCallback, useEffect, useMemo, useReducer, useRef, useState } from "react";

import { useMutation, useQuery, useQueryClient } from "../../queries/react-query";
import type {
	BorgAgentRunBrief,
	BorgConversationCreateRequest,
	BorgConversationMessageRequest,
	BorgMessage,
	BorgStatusToolCall,
} from "../models/borg-types";
import {
	STATUS_POLL_INTERVAL,
	TERMINAL_STATUSES,
	mutationBorgCancelConversation,
	mutationBorgCreateConversation,
	mutationBorgSendMessage,
	queryBorgConversationStatus,
	queryBorgConversationTranscript,
} from "../queries/borg-conversations";

import { derivePollingPhase, extractErrorMessage, isStaleConversationError } from "./_borg-lifecycle-types";
import { buildLifecycleResult } from "./_build-lifecycle-result";
import {
	INITIAL_CONVERSATION_MACHINE_STATE,
	conversationLifecycleReducer,
	getConversationId,
	selectConversationFooterInput,
} from "./_conversation-lifecycle-machine";
import type { UseConversationLifecycleResult } from "./_conversation-lifecycle-types";
import { areEquivalentParts } from "./_message-thread-utils";
import { useBorgStream } from "./_use-borg-stream";

// Stable empty array references to avoid unnecessary re-renders
const EMPTY_MESSAGES: ReadonlyArray<BorgMessage> = [];
const EMPTY_AGENT_RUNS: ReadonlyArray<BorgAgentRunBrief> = [];
const EMPTY_PROGRESS: ReadonlyArray<string> = [];
const EMPTY_TOOL_CALLS: ReadonlyArray<BorgStatusToolCall> = [];
const STREAMING_FALLBACK_INTERVAL = 10_000; // 10s fallback when SSE is active

export type { ConversationLifecyclePhase, UseConversationLifecycleResult } from "./_conversation-lifecycle-types";

export interface UseConversationLifecycleOptions {
	onStaleSession?: () => void;
}

interface SelectCurrentMessageParams {
	phase: UseConversationLifecycleResult["phase"];
	hasPersistedAssistant: boolean;
	streamingMessage: BorgMessage | undefined;
	statusCurrentMessage: BorgMessage | undefined;
	isStreaming: boolean;
}

const getMessageCreatedAt = (message: BorgMessage | undefined): number | undefined =>
	typeof message?.created_at === "number" ? message.created_at : undefined;

interface MessageProgressSnapshot {
	partCount: number;
	terminalToolCalls: number;
	totalToolCalls: number;
	textAndThinkingChars: number;
}

interface PreservedConversationMessages {
	conversationId: string;
	messages: Array<BorgMessage>;
}

const getMessageProgressSnapshot = (message: BorgMessage | undefined): MessageProgressSnapshot => {
	const initial: MessageProgressSnapshot = {
		partCount: message?.parts.length ?? 0,
		terminalToolCalls: 0,
		totalToolCalls: 0,
		textAndThinkingChars: 0,
	};
	if (message == null) {
		return initial;
	}
	return message.parts.reduce<MessageProgressSnapshot>((acc, part) => {
		if (part.type === "tool_call") {
			acc.totalToolCalls += 1;
			if (part.tool_status === "complete" || part.tool_status === "failed") {
				acc.terminalToolCalls += 1;
			}
			return acc;
		}
		if (part.type === "text" || part.type === "thinking") {
			acc.textAndThinkingChars += part.content?.length ?? 0;
		}
		return acc;
	}, initial);
};

const pickMoreAdvancedMessage = (left: BorgMessage, right: BorgMessage): BorgMessage => {
	const leftProgress = getMessageProgressSnapshot(left);
	const rightProgress = getMessageProgressSnapshot(right);
	if (leftProgress.terminalToolCalls !== rightProgress.terminalToolCalls) {
		return leftProgress.terminalToolCalls > rightProgress.terminalToolCalls ? left : right;
	}
	if (leftProgress.totalToolCalls !== rightProgress.totalToolCalls) {
		return leftProgress.totalToolCalls > rightProgress.totalToolCalls ? left : right;
	}
	if (leftProgress.partCount !== rightProgress.partCount) {
		return leftProgress.partCount > rightProgress.partCount ? left : right;
	}
	if (leftProgress.textAndThinkingChars !== rightProgress.textAndThinkingChars) {
		return leftProgress.textAndThinkingChars > rightProgress.textAndThinkingChars ? left : right;
	}
	return left;
};

export const selectCurrentMessage = ({
	phase,
	hasPersistedAssistant,
	streamingMessage,
	statusCurrentMessage,
	isStreaming,
}: SelectCurrentMessageParams): BorgMessage | undefined => {
	if (phase === "complete" && hasPersistedAssistant) {
		return undefined;
	}
	if (streamingMessage == null) {
		return statusCurrentMessage;
	}
	if (statusCurrentMessage == null) {
		return streamingMessage;
	}
	if (streamingMessage.id === statusCurrentMessage.id) {
		const preferred = pickMoreAdvancedMessage(statusCurrentMessage, streamingMessage);
		if (preferred === statusCurrentMessage) {
			return statusCurrentMessage;
		}
		return isStreaming ? streamingMessage : statusCurrentMessage;
	}

	const streamingCreatedAt = getMessageCreatedAt(streamingMessage);
	const statusCreatedAt = getMessageCreatedAt(statusCurrentMessage);
	if (streamingCreatedAt != null && statusCreatedAt != null && streamingCreatedAt !== statusCreatedAt) {
		return statusCreatedAt > streamingCreatedAt ? statusCurrentMessage : streamingMessage;
	}
	return isStreaming ? streamingMessage : statusCurrentMessage;
};

/**
 * Manages the full conversation lifecycle via a single reducer/state machine:
 * idle → creating → polling → complete → submitting → polling → complete ...
 */
export const useConversationLifecycle = ({
	onStaleSession,
}: UseConversationLifecycleOptions = {}): UseConversationLifecycleResult => {
	const [state, dispatch] = useReducer(conversationLifecycleReducer, INITIAL_CONVERSATION_MACHINE_STATE);
	const [preservedMessagesState, setPreservedMessagesState] = useState<PreservedConversationMessages | undefined>(
		undefined,
	);
	const conversationId = getConversationId(state);

	// Track the last create request for retry on create failures
	const lastCreateRequestRef = useRef<BorgConversationCreateRequest>();
	// Track the last send request for retry on send failures
	const lastSendRef = useRef<BorgConversationMessageRequest>();
	const lastTranscriptMessageCountRef = useRef<number | undefined>(undefined);
	const prevStreamingRef = useRef(false);
	const latestCurrentMessageRef = useRef<BorgMessage | undefined>(undefined);

	const queryClient = useQueryClient();
	const createMutation = useMutation(mutationBorgCreateConversation);
	const sendMutation = useMutation(mutationBorgSendMessage(conversationId ?? "", queryClient));
	const cancelMutation = useMutation(mutationBorgCancelConversation);
	const { mutate: createMutate, reset: createReset } = createMutation;
	const { mutate: sendMutate, reset: sendReset } = sendMutation;
	const { mutate: cancelMutate, reset: cancelReset } = cancelMutation;

	const handleStaleSession = useCallback(() => {
		lastCreateRequestRef.current = undefined;
		lastSendRef.current = undefined;
		setPreservedMessagesState(undefined);
		dispatch({ type: "STALE_SESSION_DETECTED" });
		createReset();
		sendReset();
		cancelReset();
		onStaleSession?.();
	}, [cancelReset, createReset, sendReset, onStaleSession]);

	const { streamingMessage, isStreaming } = useBorgStream(conversationId, state.phase);

	const { data, isLoading, isFetching, refetch } = useQuery({
		...queryBorgConversationStatus(conversationId),
		refetchInterval: (queryData) => {
			if (state.phase === "cancelled") {
				return false;
			}
			if (state.phase === "submitting") {
				return STATUS_POLL_INTERVAL;
			}
			if (queryData == null) {
				return false;
			}
			if (TERMINAL_STATUSES.has(queryData.status)) {
				return false;
			}
			// When SSE is streaming, reduce poll frequency (polling is just a fallback)
			return isStreaming ? STREAMING_FALLBACK_INTERVAL : STATUS_POLL_INTERVAL;
		},
		onError: (error: unknown) => {
			if (isStaleConversationError(error)) {
				handleStaleSession();
				return;
			}
			dispatch({
				type: "STATUS_FAILURE",
				error: extractErrorMessage(error, "Failed to refresh conversation status"),
			});
		},
	});
	const shouldFetchTranscript = conversationId != null && data != null && data.messages == null;
	const { data: transcriptData, refetch: refetchTranscript } = useQuery({
		...queryBorgConversationTranscript(conversationId),
		enabled: shouldFetchTranscript,
	});

	const pollingPhase = derivePollingPhase(data, isLoading);
	const persistedMessages = data?.messages ?? transcriptData?.messages ?? EMPTY_MESSAGES;
	const preservedMessages =
		preservedMessagesState != null && preservedMessagesState.conversationId === conversationId
			? preservedMessagesState.messages
			: EMPTY_MESSAGES;
	const messages = useMemo(() => {
		if (preservedMessages.length === 0) {
			return persistedMessages;
		}

		return preservedMessages.reduce<ReadonlyArray<BorgMessage>>((acc, preservedMessage) => {
			const alreadyPersisted = acc.some(
				(message) =>
					message.id === preservedMessage.id ||
					(message.role === preservedMessage.role && areEquivalentParts(message.parts, preservedMessage.parts)),
			);

			return alreadyPersisted ? acc : [...acc, preservedMessage];
		}, persistedMessages);
	}, [persistedMessages, preservedMessages]);
	const hasPersistedAssistant = messages.some((message) => message.role === "assistant");
	const currentMessage = selectCurrentMessage({
		phase: state.phase,
		hasPersistedAssistant,
		streamingMessage,
		statusCurrentMessage: data?.current_message,
		isStreaming,
	});

	useEffect(() => {
		latestCurrentMessageRef.current = currentMessage;
	}, [currentMessage]);

	useEffect(() => {
		if (
			preservedMessagesState == null ||
			conversationId == null ||
			preservedMessagesState.conversationId !== conversationId
		) {
			return;
		}

		const remainingMessages = preservedMessagesState.messages.filter(
			(preservedMessage) =>
				!persistedMessages.some(
					(message) =>
						message.id === preservedMessage.id ||
						(message.role === preservedMessage.role && areEquivalentParts(message.parts, preservedMessage.parts)),
				),
		);

		if (remainingMessages.length === preservedMessagesState.messages.length) {
			return;
		}

		setPreservedMessagesState((prev) => {
			if (prev == null || prev.conversationId !== conversationId) {
				return prev;
			}

			return remainingMessages.length === 0 ? undefined : { conversationId, messages: remainingMessages };
		});
	}, [conversationId, persistedMessages, preservedMessagesState]);

	useEffect(() => {
		if (!shouldFetchTranscript) {
			lastTranscriptMessageCountRef.current = undefined;
			return;
		}
		const messageCount = data?.message_count;
		if (messageCount == null) {
			return;
		}
		if (lastTranscriptMessageCountRef.current == null) {
			lastTranscriptMessageCountRef.current = messageCount;
			return;
		}
		if (messageCount !== lastTranscriptMessageCountRef.current) {
			lastTranscriptMessageCountRef.current = messageCount;
			refetchTranscript();
		}
	}, [data?.message_count, refetchTranscript, shouldFetchTranscript]);

	// Trigger immediate refetch when SSE stream disconnects so saved messages
	// appear promptly instead of waiting up to 10s for the next poll cycle.
	useEffect(() => {
		if (prevStreamingRef.current && !isStreaming && conversationId != null) {
			refetch();
			if (shouldFetchTranscript) {
				refetchTranscript();
			}
		}
		prevStreamingRef.current = isStreaming;
	}, [conversationId, isStreaming, refetch, refetchTranscript, shouldFetchTranscript]);

	useEffect(() => {
		const isCancelledStatus = data?.status === "cancelled" || data?.status === "canceling";

		if (isCancelledStatus && (state.phase === "polling" || state.phase === "cancelled")) {
			dispatch({ type: "CANCEL_SUCCESS" });
			return;
		}

		if (state.phase === "cancelled") {
			return;
		}

		switch (pollingPhase) {
			case "in_progress":
				dispatch({ type: "STATUS_IN_PROGRESS" });
				break;
			case "complete":
				dispatch({ type: "STATUS_COMPLETE" });
				break;
			case "error":
				dispatch({
					type: "STATUS_FAILURE",
					error: data?.response ?? data?.status ?? "Conversation failed",
				});
				break;
		}
	}, [pollingPhase, data?.response, data?.status, state.phase]);

	const start = useCallback(
		(
			message: string,
			agent?: BorgConversationCreateRequest["agent"],
			contextPins?: BorgConversationCreateRequest["context_pins"],
		) => {
			const params: BorgConversationCreateRequest = { message };
			if (agent != null) {
				params.agent = agent;
			}
			if (contextPins != null && contextPins.length > 0) {
				params.context_pins = contextPins;
			}

			lastCreateRequestRef.current = params;
			lastSendRef.current = undefined;
			dispatch({ type: "START_REQUEST" });
			createMutate(params, {
				onSuccess: (response) => {
					dispatch({ type: "START_SUCCESS", conversationId: response.id });
				},
				onError: (error) => {
					dispatch({
						type: "START_FAILURE",
						error: extractErrorMessage(error, "Unknown error"),
					});
				},
			});
		},
		[createMutate],
	);

	const sendMessage = useCallback(
		(
			message: string,
			agent?: BorgConversationMessageRequest["agent"],
			contextPins?: BorgConversationMessageRequest["context_pins"],
		) => {
			if (conversationId == null) {
				return;
			}

			const params: BorgConversationMessageRequest = { message };
			if (agent != null) {
				params.agent = agent;
			}
			if (contextPins != null && contextPins.length > 0) {
				params.context_pins = contextPins;
			}

			lastSendRef.current = params;
			dispatch({ type: "SEND_REQUEST", message });

			sendMutate(params, {
				onSuccess: () => {
					dispatch({ type: "SEND_SUCCESS" });
				},
				onError: (error) => {
					if (isStaleConversationError(error)) {
						handleStaleSession();
						return;
					}
					dispatch({
						type: "SEND_FAILURE",
						error: extractErrorMessage(error, "Failed to send message"),
					});
				},
			});
		},
		[conversationId, sendMutate, handleStaleSession],
	);

	const retrySendMessage = useCallback(() => {
		if (lastSendRef.current == null || conversationId == null) {
			return;
		}
		const params = lastSendRef.current;
		const { message } = params;
		sendReset();
		dispatch({ type: "SEND_REQUEST", message });

		sendMutate(params, {
			onSuccess: () => {
				dispatch({ type: "SEND_SUCCESS" });
			},
			onError: (error) => {
				if (isStaleConversationError(error)) {
					handleStaleSession();
					return;
				}
				dispatch({
					type: "SEND_FAILURE",
					error: extractErrorMessage(error, "Failed to send message"),
				});
			},
		});
	}, [conversationId, sendMutate, sendReset, handleStaleSession]);

	const retry = useCallback(() => {
		if (lastCreateRequestRef.current == null) {
			return;
		}
		const params = lastCreateRequestRef.current;
		createReset();
		dispatch({ type: "START_REQUEST" });
		createMutate(params, {
			onSuccess: (response) => {
				dispatch({ type: "START_SUCCESS", conversationId: response.id });
			},
			onError: (error) => {
				dispatch({
					type: "START_FAILURE",
					error: extractErrorMessage(error, "Unknown error"),
				});
			},
		});
	}, [createMutate, createReset]);

	const cancelCurrentTurn = useCallback(() => {
		if (conversationId == null) {
			return;
		}

		const statusQueryKey = queryBorgConversationStatus(conversationId).queryKey;
		const transcriptQueryKey = queryBorgConversationTranscript(conversationId).queryKey;
		const messageToPreserve = latestCurrentMessageRef.current;

		cancelMutate(conversationId, {
			onSuccess: () => {
				if (messageToPreserve != null && messageToPreserve.parts.length > 0) {
					setPreservedMessagesState((prev) => {
						const existingMessages = prev?.conversationId === conversationId ? prev.messages : [];
						const alreadyPreserved = existingMessages.some(
							(message) =>
								message.id === messageToPreserve.id ||
								(message.role === messageToPreserve.role && areEquivalentParts(message.parts, messageToPreserve.parts)),
						);

						if (alreadyPreserved) {
							return prev;
						}

						return {
							conversationId,
							messages: [...existingMessages, messageToPreserve],
						};
					});
				}

				dispatch({ type: "CANCEL_SUCCESS" });
				queryClient.invalidateQueries([{}, ...statusQueryKey]);
				queryClient.invalidateQueries([{}, ...transcriptQueryKey]);
			},
			onError: (error) => {
				if (isStaleConversationError(error)) {
					handleStaleSession();
					return;
				}
				dispatch({
					type: "CANCEL_FAILURE",
					error: extractErrorMessage(error, "Failed to stop response"),
				});
			},
		});
	}, [cancelMutate, conversationId, handleStaleSession, queryClient]);

	const loadExisting = useCallback((id: string) => {
		lastCreateRequestRef.current = undefined;
		lastSendRef.current = undefined;
		setPreservedMessagesState(undefined);
		dispatch({ type: "LOAD_EXISTING", conversationId: id });
	}, []);

	const reset = useCallback(() => {
		lastCreateRequestRef.current = undefined;
		lastSendRef.current = undefined;
		setPreservedMessagesState(undefined);
		dispatch({ type: "RESET" });
		createReset();
		sendReset();
		cancelReset();
	}, [cancelReset, createReset, sendReset]);

	const footerInput = useMemo(
		() => selectConversationFooterInput(state, sendMutation.isLoading),
		[state, sendMutation.isLoading],
	);

	return buildLifecycleResult(
		state,
		{
			start,
			sendMessage,
			retrySendMessage,
			retry,
			refetch,
			cancelCurrentTurn,
			loadExisting,
			reset,
			isFetching,
			isSubmittingMessage: sendMutation.isLoading,
			footerInput,
			messages,
			currentMessage,
			activeAgent: data?.active_agent,
			agentRuns: data?.agent_runs ?? EMPTY_AGENT_RUNS,
			progressUpdates: EMPTY_PROGRESS,
			toolCalls: data?.recent_tool_calls ?? EMPTY_TOOL_CALLS,
			startedAt: data?.started_at,
			completedAt: data?.completed_at,
		},
		data?.response,
	);
};
